import type { ReactNode } from "react";
import { EntityDrilldownSkeleton } from "src/pages/ad-studio/insights/entity-drilldown-skeleton";
import { EntityDrilldownPrimitive } from "src/pages/ad-studio/insights/entity-drilldown-primitive";
import { FailureAlert } from "src/pages/ad-studio/insights/performance/failure-alert";
import type {
  DrillScope,
  DrillScopeResult,
  ParentGrain,
} from "src/pages/ad-studio/insights/performance/types";
import { useEntityDrilldown } from "src/pages/ad-studio/insights/use-entity-drilldown";

type EntityDrilldownProps = {
  drill: DrillScopeResult;
  grain: ParentGrain;
  id: string;
  /** The parts to show once the scope loads; the host picks whether child rows drill down. */
  children?: ReactNode;
};

const LoadedEntityDrilldown = ({
  drill,
  grain,
  id,
  children,
}: Omit<EntityDrilldownProps, "drill"> & { drill: DrillScope }) => {
  const { state, performAction } = useEntityDrilldown({ drill, grain, id });
  return (
    <EntityDrilldownPrimitive.Root
      header={state.header}
      notFoundHeading={state.notFoundHeading}
      childList={state.childList}
      performAction={performAction}
    >
      {children}
    </EntityDrilldownPrimitive.Root>
  );
};

export const EntityDrilldown = ({
  drill,
  grain,
  id,
  children,
}: EntityDrilldownProps) => {
  switch (drill.status) {
    case "loading": {
      return <EntityDrilldownSkeleton />;
    }
    case "failed": {
      return <FailureAlert failure={drill.failure} />;
    }
    case "loaded": {
      return (
        <LoadedEntityDrilldown drill={drill.value} grain={grain} id={id}>
          {children}
        </LoadedEntityDrilldown>
      );
    }
    default: {
      return drill satisfies never;
    }
  }
};
